/**
 * Service Worker registration + local SQLite runtime bootstrap.
 */
import type { Database } from 'sql.js';
import { AppConfig } from '@core/domain/AppConfig.ts';
import { withRetry } from '@core/domain/utils/asyncResilience.ts';
import type { LocalSqliteRuntime } from '@src/modules/platform/local-sqlite/domain/LocalSqliteRuntime.ts';
import {
  openLocalSqliteSession,
  reloadLocalSqliteSessionFromIdb,
  type LocalSqliteSession,
} from '@src/modules/platform/local-sqlite/infrastructure/loadLocalSqlDatabase.ts';
import {
  bindLocalSqliteDirtyNotifier,
  isLocalSqliteDirty,
  resetLocalSqlitePersistState,
} from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePersistDirty.ts';
import { dispatchLocalSqlitePeerUpdate } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePeerSync.ts';
import { bindLocalSqlitePeerSync } from '@src/modules/platform/local-sqlite/infrastructure/localSqlitePeerSync.ts';
import { bindLocalSqliteUnloadFlush } from '@src/modules/platform/local-sqlite/infrastructure/localSqliteUnloadFlush.ts';
import { bindCapacitorAppPauseFlush } from '@src/modules/platform/local-sqlite/infrastructure/localSqliteCapacitorFlush.ts';
import { registerOfflineServiceWorkerFromEnv } from '@src/modules/platform/local-sqlite/infrastructure/offlineServiceWorkerClient.ts';

export type OfflineSqliteInfraHandles = {
  getRuntime(): LocalSqliteRuntime;
  bootstrap(): Promise<void>;
};

function createLocalSqliteRuntime(): LocalSqliteRuntime {
  let db: Database | undefined;
  let persist: () => void = () => {};
  let flushPersistImpl: () => Promise<void> = async () => {};
  let lastError: string | undefined;

  return {
    getDb: () => db,
    notifyPersist: () => {
      try {
        persist();
      } catch (e) {
        // oxlint-disable-next-line no-console -- persist may throw if IDB/SQL fails at runtime
        console.warn('[local-sqlite] notifyPersist', e);
      }
    },
    flushPersist: async () => {
      try {
        await flushPersistImpl();
      } catch (e) {
        // oxlint-disable-next-line no-console -- persist may throw if IDB/SQL fails at runtime
        console.warn('[local-sqlite] flushPersist', e);
        throw e;
      }
    },
    setSession: (d, p, flush) => {
      db = d;
      persist = p;
      flushPersistImpl = flush ?? (async () => {
        p();
      });
      lastError = undefined;
    },
    signalConnectionFailed: (message: string) => {
      lastError = message;
    },
    getConnectionHealth: () => ({
      ready: db != null,
      lastError,
    }),
    resetForTests: () => {
      db = undefined;
      persist = () => {};
      flushPersistImpl = async () => {};
      lastError = undefined;
      resetLocalSqlitePersistState();
    },
  };
}

async function bootstrapLocalSqlite(runtime: LocalSqliteRuntime): Promise<LocalSqliteSession> {
  const session = await openLocalSqliteSession(AppConfig.getLocalSqliteSnapshotUrl());
  runtime.setSession(session.db, session.persist, session.flushPersist);
  bindLocalSqliteDirtyNotifier(() => runtime.notifyPersist());
  bindLocalSqliteUnloadFlush(() => runtime.flushPersist());
  bindCapacitorAppPauseFlush(() => runtime.flushPersist());
  bindLocalSqlitePeerSync(async () => {
    if (isLocalSqliteDirty()) return;
    const reloaded = await reloadLocalSqliteSessionFromIdb(session);
    Object.assign(session, reloaded);
    runtime.setSession(session.db, session.persist, session.flushPersist);
    dispatchLocalSqlitePeerUpdate();
  });
  return session;
}

/** SQLite + service worker bootstrap; call `bootstrap()` before using the runtime. */
export function createOfflineSqliteInfrastructure(): OfflineSqliteInfraHandles {
  const runtime = createLocalSqliteRuntime();

  return {
    getRuntime: () => runtime,
    async bootstrap(): Promise<void> {
      // Safari/iOS: sin persist(), ITP borra IndexedDB (BD + media) tras 7 días sin uso.
      void globalThis.navigator?.storage?.persist?.().catch(() => {});
      await registerOfflineServiceWorkerFromEnv(import.meta.env);
      if (!AppConfig.useLocalSqliteClient()) {
        await bootstrapLocalSqlite(runtime);
        return;
      }
      await withRetry(() => bootstrapLocalSqlite(runtime), {
        maxAttempts: 4,
        baseDelayMs: 500,
        maxDelayMs: 10_000,
        jitterRatio: 0.2,
        isRetryable: (err) => {
          const m = err instanceof Error ? err.message : String(err);
          if (/\(404\)|\(403\)/.test(m)) return false;
          if (/db:init-local/i.test(m)) return false;
          return true;
        },
      });
    },
  };
}
